import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const faqs = [
  {
    q: 'How much does DanceHall Studios cost?',
    a: 'Free for schools — always. Each student payment adds a flat 2.99% DanceHall fee, and you keep 100% of the price you set. When students pay by ACH bank transfer, processing costs drop significantly, and anything left over comes back to you as cash back.',
  },
  {
    q: 'How do payments work?',
    a: 'Powered by Stripe. Students pay online with credit card or ACH bank transfer. Monthly recurring, quarterly, or one-time — money goes directly to your bank account.',
  },
  {
    q: 'Can I pause students who travel for the summer?',
    a: 'Yes. Pause any student without cancelling the relationship — their billing stops, their access holds, and you can unpause them the moment they return. Common for long-term students who spend summers in India.',
  },
  {
    q: 'Can I organize courses by level and promote students between levels?',
    a: 'Yes. Structure your curriculum by level, track each student\u2019s progress, and promote them at the next billing cycle with a single click. Promotion emails go out automatically.',
  },
  {
    q: 'How do announcements and newsletters work?',
    a: 'Send class-wide or school-wide emails from your own sender address with rich text, images, and pre-built templates. Everything goes out through a proper inbox trail — no WhatsApp group scroll required.',
  },
  {
    q: 'Is there a contract or lock-in?',
    a: "No contracts, no lock-in, no hidden fees. You can stop using DanceHall Studios at any time. Your students' payments are processed directly through your Stripe account.",
  },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [openTooltip, setOpenTooltip] = useState(null);
  const { login } = useAuth();

  const signupAs = (role) => {
    sessionStorage.setItem('dancehall_signup_role', role);
    login({ mode: 'signup' });
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="min-h-screen bg-[#faf0f0] font-body">

      {/* ─── Nav ─── */}
      <nav className="fixed top-0 w-full bg-[#faf0f0]/80 backdrop-blur-md z-50 border-b border-gray-200/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="relative inline-block"><span className="font-heading text-2xl font-bold tracking-tight text-gray-900 relative z-10">DanceHall</span><span className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-brand-900 whitespace-nowrap z-0" style={{ fontFamily: "'Dancing Script', cursive", fontWeight: 1000, fontSize: '22px' }}>Studios</span></span>

          <div className="hidden md:flex items-center gap-8 text-[13px] text-gray-500">
            <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-gray-900 transition-colors">FAQ</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => login()}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Log in
            </button>
            <button
              onClick={() => signupAs('teacher')}
              className="text-sm font-medium bg-brand-900 text-white px-5 py-2 rounded-full hover:bg-brand-800 transition-colors"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-32 px-6 relative overflow-hidden">
        {/* Subtle gradient wash */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-gradient-to-br from-brand-100/40 via-transparent to-saffron-100/30 rounded-full blur-3xl pointer-events-none" />

        {/* TODO: pencil-drawn 2D hero illustration goes here (absolute, behind content) */}

        <div className="max-w-4xl mx-auto text-center relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200/80 text-brand-800 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <span>Built for Indian classical dance schools</span>
          </div>

          <h1 className="font-heading text-5xl md:text-7xl text-gray-900 mb-8 leading-[1.08]">
            Grow your Indian classical
            <br />
            <span className="text-brand-900">dance school.</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Payments, newsletters, class reminders, promotions, and student pauses
            &mdash; handled. Parents and students log in directly from your website.
            So you can focus on teaching, not chasing spreadsheets and WhatsApp threads.
          </p>

          <div className="flex items-center justify-center">
            <button
              onClick={() => signupAs('teacher')}
              className="bg-brand-900 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-brand-800 transition-all hover:shadow-lg hover:shadow-brand-900/25"
            >
              Create Your Studio
            </button>
          </div>

        </div>
      </section>

      {/* ─── Social Proof ─── */}
      <section className="pt-2 pb-16 md:pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-6">
            Indian classical schools running on DanceHall
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10">
            <a
              href="https://www.templeoffineartstexas.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading text-xl md:text-2xl text-gray-900 hover:text-brand-900 transition-colors"
            >
              Temple of Fine Arts Texas
            </a>
            <span className="hidden sm:block text-brand-200 text-2xl leading-none">&middot;</span>
            <a
              href="https://hiphopnatyam.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-heading text-xl md:text-2xl text-gray-900 hover:text-brand-900 transition-colors"
            >
              HipHopNatyam
            </a>
          </div>
        </div>
      </section>

      {/* ─── Competitor Comparison ─── */}
      <section className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mb-4">See how we compare</h2>
            <p className="text-gray-500 text-lg">Most studio software charges you monthly before you earn a dime.</p>
          </div>

          {/* Feature comparison grid */}
          <div>
            <p className="text-gray-400 text-sm text-center mb-6">Scroll right to see all providers &rarr;</p>

            <div className="overflow-x-auto -mx-6 px-6 pb-4">
              <table className="w-full text-sm" style={{ minWidth: '1100px' }}>
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 pr-4 font-medium text-gray-400 w-[200px] sticky left-0 bg-white z-10">Feature</th>
                    <th className="py-3 px-3 font-semibold text-white bg-brand-900 text-center rounded-t-lg w-[110px]">DanceHall</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">ClassManager</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">Mindbody</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">Vagaro</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">Jackrabbit</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">WellnessLiving</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">Pike13</th>
                    <th className="py-3 px-3 font-medium text-gray-700 text-center w-[110px]">Zen Planner</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Monthly cost', vals: ['$0', '$0', '$139–$700+', '$30–$90', '$49–$331', '$69–$349', '$139–$286', '$99–$289+'] },
                    { feature: 'Transaction fees', tooltip: 'Studios pay $0 from their payout — teachers always receive 100% of their quoted price. Students pay a single all-inclusive fee that includes a 2.99% DanceHall platform fee plus the payment processor fee (~3% for cards, industry-standard Stripe rate). One price upfront, no surprise charges.', vals: ['2.99%', '3.1%+30¢', '2.99%+30¢', '2.2%+10¢', '2.59%+30¢', 'Varies', 'Undisclosed', '2.97%+47¢'] },
                    { feature: 'Setup fee', vals: ['$0', '$0', 'Undisclosed', '$0', '$0', '$0', '$0', '$499'] },
                    { feature: 'Hidden fees', vals: ['None', '80% lock-in', '20% mktplace', 'Add-ons', 'Per student', '$199/yr + PCI', 'App fees', 'CRM $249/mo'] },
                    { feature: 'QR code attendance', vals: ['yes', 'no', 'no', 'no', 'no', 'no', 'no', 'no'] },
                    { feature: 'Class materials / videos', vals: ['yes', 'no', 'no', 'no', 'no', 'no', 'no', 'no'] },
                    { feature: 'Video watermarking', vals: ['yes', 'no', 'no', 'no', 'no', 'no', 'no', 'no'] },
                    { feature: 'Custom AI support', tooltip: 'A studio-trained AI chat sits on every enrollment page. It answers prospect questions about your offering using the studio context you provide (refund policy, schedule, what to bring, etc.) — so families get answers instantly instead of waiting for an email reply. If the AI is unsure or the question is personal, it routes the prospect to email the studio directly.', vals: ['upon request', 'no', 'no', 'no', 'no', 'no', 'no', 'no'] },
                    { feature: 'WhatsApp integration', vals: ['yes', 'no', 'no', 'no', 'no', 'no', 'no', 'no'] },
                    { feature: 'Newsletter', vals: ['yes', 'no', 'paid', 'paid', 'no', 'paid', 'paid', 'paid'] },
                    { feature: 'SMS notifications', vals: ['upon request', 'paid', 'paid', 'paid', 'paid', 'paid', 'yes', 'paid'] },
                    { feature: 'Zoom integration', vals: ['yes', 'no', 'yes', 'no', 'no', 'yes', 'no', 'yes'] },
                    { feature: 'Free studio website', vals: ['yes', 'no', 'no', 'paid', 'no', 'no', 'no', 'paid'] },
                    { feature: 'Shop / merchandise', vals: ['upon request', 'no', 'yes', 'yes', 'no', 'yes', 'no', 'no'] },
                    { feature: 'Course levels & promotion', vals: ['yes', 'no', 'no', 'no', 'partial', 'no', 'no', 'no'] },
                    { feature: 'Cash back program', tooltip: 'When a student pays by ACH bank transfer, the payment processor charges only 0.8% instead of ~3% for cards. The savings — about $2 back per $100 collected — get credited to your DanceHall cashback balance. The more your students pay by ACH, the more you earn.', vals: ['yes', 'no', 'no', 'no', 'no', 'no', 'no', 'no'] },
                    { feature: 'Costume management', vals: ['upon request', 'yes', 'no', 'no', 'yes', 'no', 'no', 'no'] },
                    { feature: 'Recital planning', vals: ['upon request', 'yes', 'no', 'no', 'yes', 'no', 'no', 'no'] },
                    { feature: 'Waitlist management', vals: ['upon request', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes'] },
                    { feature: 'Mobile app', vals: ['upon request', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes'] },
                    { feature: 'Multi-location', vals: ['upon request', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes'] },
                    { feature: 'Staff management', vals: ['upon request', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes', 'yes'] },
                  ].map((row, i) => (
                    <tr key={row.feature} className={`border-b border-gray-50 ${openTooltip === row.feature ? 'relative z-40' : ''} ${i % 2 === 0 ? 'bg-gray-50/50' : ''}`}>
                      <td className={`py-3 pr-4 font-medium text-gray-700 sticky left-0 ${openTooltip === row.feature ? 'z-40' : 'z-10'} ${i % 2 === 0 ? 'bg-gray-50/80' : 'bg-white'}`}>
                        <span className="inline-flex items-center gap-1.5">
                          {row.feature}
                          {row.tooltip && (
                            <span className="relative inline-block">
                              <button
                                type="button"
                                onClick={() => setOpenTooltip(openTooltip === row.feature ? null : row.feature)}
                                onBlur={() => setTimeout(() => setOpenTooltip((cur) => (cur === row.feature ? null : cur)), 150)}
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold hover:bg-brand-100 hover:text-brand-700 transition-colors"
                                aria-label={`More info about ${row.feature}`}
                              >
                                i
                              </button>
                              {openTooltip === row.feature && (
                                <span className="absolute left-0 top-6 z-50 w-64 sm:w-72 p-3 bg-gray-900 text-white text-xs leading-relaxed rounded-lg shadow-xl normal-case font-normal">
                                  {row.tooltip}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </td>
                      {row.vals.map((val, j) => (
                        <td key={j} className={`py-3 px-3 text-center ${j === 0 ? 'bg-brand-900/5 font-medium' : ''}`}>
                          {val === 'yes' ? (
                            <svg className={`w-5 h-5 mx-auto ${j === 0 ? 'text-brand-900' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                          ) : val === 'no' ? (
                            <svg className="w-5 h-5 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          ) : val === 'upon request' ? (
                            <span className="text-xs text-brand-500 font-medium">Upon request</span>
                          ) : val === 'paid' ? (
                            <span className="text-xs text-amber-500 font-medium">Paid add-on</span>
                          ) : val === 'partial' ? (
                            <span className="text-xs text-gray-400 font-medium">Partial</span>
                          ) : (
                            <span className={`${j === 0 ? 'text-brand-900 font-semibold' : 'text-gray-600'}`}>{val}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Features (bento grid) ─── */}
      <section id="features" className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl text-gray-900 mb-4">
              Everything you need, all in one place
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Built for Indian classical dance schools. Every feature exists because a guru or school owner needed it.
            </p>
          </div>

          {/* Row 1 — two large cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Payments */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 md:p-10 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-heading text-2xl text-gray-900 mb-3">Payments on autopilot</h3>
              <p className="text-gray-500 leading-relaxed mb-4">
                Powered by Stripe. Monthly recurring, one-time, or block pricing. Credit card and
                ACH bank transfer. Money goes directly to your bank account.
              </p>
              <p className="text-sm font-medium text-brand-900">Never forget anyone's payment again.</p>
            </div>

            {/* Attendance */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 md:p-10 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-8 h-8 text-brand-900 mb-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}>
                <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" />
                <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" />
                <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" />
                <rect x="14" y="14" width="3" height="3" stroke="currentColor" />
                <rect x="18" y="14" width="3" height="3" stroke="currentColor" />
                <rect x="14" y="18" width="3" height="3" stroke="currentColor" />
                <rect x="18" y="18" width="3" height="3" stroke="currentColor" />
                <rect x="5.5" y="5.5" width="2" height="2" fill="currentColor" />
                <rect x="16.5" y="5.5" width="2" height="2" fill="currentColor" />
                <rect x="5.5" y="16.5" width="2" height="2" fill="currentColor" />
              </svg>
              <h3 className="font-heading text-2xl text-gray-900 mb-3">QR code attendance</h3>
              <p className="text-gray-500 leading-relaxed mb-4">
                Students scan a code when they walk in. Done in 15 seconds.
                Zoom classes auto-tracked. Full attendance history, reports, and analytics built in.
              </p>
              <p className="text-sm font-medium text-brand-900">In-person + online, one system.</p>
            </div>
          </div>

          {/* Row 2 — three medium cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {/* Materials */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Class materials</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Upload tutorials once. Videos unlock week by week.
                Watermarked with each student's name. No raw links shared.
              </p>
            </div>

            {/* WhatsApp + Email */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">WhatsApp & Email</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Class reminders, payment nudges, enrollment confirmations.
                Students get notified where they already are.
              </p>
            </div>

            {/* Zoom */}
            <div className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Zoom integration</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Create meetings, auto-track attendance.
                In-person, online, or hybrid &mdash; one click to join.
              </p>
            </div>
          </div>

          {/* Row 2.5 — four small cards: Newsletters, Reminders, Promotion, Pause */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Newsletters & announcements */}
            <div className="bg-[#faf0f0] rounded-2xl p-6 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Newsletters</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Class-wide or school-wide emails from your own sender address. Rich text, images, and templates built for dance schools.
              </p>
            </div>

            {/* Class reminders */}
            <div className="bg-[#faf0f0] rounded-2xl p-6 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Class reminders</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Automatic day-before emails for every class, plus week-before and day-before reminders for assessments.
              </p>
            </div>

            {/* Class promotion */}
            <div className="bg-[#faf0f0] rounded-2xl p-6 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Class promotion</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Promote students between levels at the next billing cycle. Level-up emails and pricing transitions happen automatically.
              </p>
            </div>

            {/* Pause students */}
            <div className="bg-[#faf0f0] rounded-2xl p-6 border border-gray-100 group hover:border-brand-200 transition-colors">
              <svg className="w-7 h-7 text-brand-900 mb-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9v6m-4.5 0V9M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Pause students</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Pause a student for summer in India without cancelling the relationship. Billing stops, access holds, unpause in one click.
              </p>
            </div>
          </div>

          {/* Row 3 — full-width accent card */}
          <div className="bg-brand-900 rounded-2xl p-8 md:p-10 text-white">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-brand-100 px-3 py-1 rounded-full text-xs font-medium mb-4 uppercase tracking-wider">
                Studio services
              </div>
              <h3 className="font-heading text-2xl md:text-3xl mb-3">We don't just give you a website. We run it with you.</h3>
              <p className="text-brand-100 leading-relaxed max-w-2xl">
                Your enrollment page is free and launches in minutes. Need more &mdash; a custom
                domain, updates on demand, or help getting found online? We do all of it, for
                less than you're paying Wix, Squarespace, or GoDaddy.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="border-t border-white/15 pt-5">
                <svg className="w-6 h-6 text-brand-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 17.25v-.228a4.5 4.5 0 00-.12-1.03l-2.268-9.64a3.375 3.375 0 00-3.285-2.602H7.923a3.375 3.375 0 00-3.285 2.602l-2.268 9.64a4.5 4.5 0 00-.12 1.03v.228m19.5 0a3 3 0 01-3 3H5.25a3 3 0 01-3-3m19.5 0a3 3 0 00-3-3H5.25a3 3 0 00-3 3m16.5 0h.008v.008h-.008v-.008zm-3 0h.008v.008h-.008v-.008z" />
                </svg>
                <div className="text-brand-200 text-xs uppercase tracking-wider mb-2">Hosting</div>
                <h4 className="font-semibold text-lg text-white mb-2">Hosted & always up</h4>
                <p className="text-brand-200 text-sm leading-relaxed">
                  We host your site on fast, secure infrastructure and keep it maintained in the
                  background. Cheaper than GoDaddy, Wix, or Squarespace &mdash; and someone is
                  always watching it for you.
                </p>
              </div>
              <div className="border-t border-white/15 pt-5">
                <svg className="w-6 h-6 text-brand-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
                <div className="text-brand-200 text-xs uppercase tracking-wider mb-2">Maintenance</div>
                <h4 className="font-semibold text-lg text-white mb-2">Updates on demand</h4>
                <p className="text-brand-200 text-sm leading-relaxed">
                  Want to swap out a photo, add a new instructor, or change your schedule? Send it
                  over &mdash; we update your site for you. No tickets, no learning curves, no
                  CMS to wrestle with.
                </p>
              </div>
              <div className="border-t border-white/15 pt-5">
                <svg className="w-6 h-6 text-brand-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A9.005 9.005 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                </svg>
                <div className="text-brand-200 text-xs uppercase tracking-wider mb-2">Visibility</div>
                <h4 className="font-semibold text-lg text-white mb-2">Google & ChatGPT visibility</h4>
                <p className="text-brand-200 text-sm leading-relaxed">
                  We configure meta tags, sitemaps, and structured data so local students find you
                  on Google &mdash; and so ChatGPT and other AI tools actually know you exist and
                  can recommend you.
                </p>
              </div>
            </div>

            <button
              onClick={() => signupAs('teacher')}
              className="bg-white text-brand-900 px-6 py-3 rounded-full font-semibold hover:bg-brand-50 transition-colors"
            >
              See it in action
            </button>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900">Up and running in 10 minutes</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
            {/* Connector line (desktop only) */}
            <div className="hidden md:block absolute top-7 left-[calc(16.67%+28px)] right-[calc(16.67%+28px)] h-px bg-gray-200" />

            {[
              { n: '1', title: 'Create your studio', desc: 'Set up your profile, add your programs and courses. Name your levels, set your schedule and pricing — you keep 100% of what you charge.' },
              { n: '2', title: 'Connect Stripe', desc: 'One click, about 2 minutes. Payouts go straight to your bank. We add a flat 2.99% to each student payment — that\u2019s it.' },
              { n: '3', title: 'Share your link', desc: 'Students enroll and pay online. You focus on teaching. We handle the rest.' },
            ].map((step) => (
              <div key={step.n} className="text-center relative">
                <div className="w-14 h-14 bg-white border-2 border-brand-200 rounded-2xl flex items-center justify-center mx-auto mb-6 relative z-10">
                  <span className="font-heading text-2xl text-brand-500">{step.n}</span>
                </div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* ─── White-glove onboarding callout ─── */}
          <div className="mt-16 md:mt-20 bg-white border border-brand-200/80 rounded-2xl p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200/80 text-brand-800 px-3 py-1 rounded-full text-xs font-medium mb-4 uppercase tracking-wider">
                  White-glove setup
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-gray-900 mb-3">
                  Or skip the setup &mdash; we'll do it for you.
                </h3>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Tell us your studio's programs and prices and we'll build your site and upload
                  everything. If you already have students, we migrate them in under an hour and
                  request payment from them on your behalf.
                </p>
                <button
                  onClick={() => signupAs('teacher')}
                  className="bg-brand-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-800 transition-colors"
                >
                  Book white-glove setup
                </button>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 bg-brand-50 border border-brand-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-brand-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L10.16 3.66A2.25 2.25 0 008.568 3H9.568z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">We set up your programs &amp; pricing</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Send us your class list and what you charge. We build it for you and push it live.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 bg-brand-50 border border-brand-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-brand-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Student migration in under an hour</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Existing roster? We'll import them and email each student to pay on your behalf.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Who It's For ─── */}
      <section className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mb-4">Built for schools like yours</h2>
            <p className="text-gray-500 text-lg">Whether you&rsquo;re starting out or running multiple branches, we fit the way classical schools actually grow.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                name: 'Solo teacher scaling up',
                desc: 'One teacher, a growing student list, and no time for spreadsheets. Autopay, attendance, and announcements take care of the operations so you can focus on class.',
              },
              {
                name: 'Established academy',
                desc: 'Multi-level curriculum, assessments, and recitals. Structure your levels, promote students at the right time, and keep everything in one place.',
              },
              {
                name: 'Multi-city expansion',
                desc: 'Multiple branches or co-teachers across cities. One roster, one reporting view, unified payments &mdash; even as you grow beyond your first studio.',
              },
            ].map((persona) => (
              <div key={persona.name} className="p-8 rounded-2xl bg-[#faf0f0] border border-gray-100 hover:border-brand-200 transition-colors">
                <h3 className="font-heading text-xl text-gray-900 mb-3">{persona.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{persona.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-5xl text-gray-900 mb-4">Free for schools. Always.</h2>
            <p className="text-gray-500 text-lg">
              No tiers. No upsells. Every feature included.
              <br />
              We add a flat <span className="font-semibold text-gray-900">2.99%</span> to each student payment &mdash; that's all. You keep 100% of your price.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10">
            <div className="flex items-baseline gap-2 mb-8">
              <span className="font-heading text-5xl text-gray-900">$0</span>
              <span className="text-gray-400 text-lg">/month, forever</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10">
              {[
                'Unlimited courses & students',
                'Stripe-powered payments',
                'Newsletters & announcements',
                'Class reminders (email)',
                'Level promotion',
                'Pause students anytime',
                'QR code attendance',
                'Class materials & videos',
                'WhatsApp integration',
                'Free studio website',
                'Zoom integration',
                'Cash back program',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-brand-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span className="text-gray-600">{feature}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => signupAs('teacher')}
              className="w-full bg-brand-900 text-white py-4 rounded-full font-semibold text-lg hover:bg-brand-800 transition-colors"
            >
              Create Your Studio
            </button>

          </div>

          {/* Transparent pricing callout */}
          <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-8">
            <h4 className="font-heading text-xl text-gray-900 mb-2">Transparent pricing</h4>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Each payment adds a flat 2.99% DanceHall fee &mdash; nothing more. When students pay by ACH
              bank transfer, processing costs drop significantly, and anything left over comes back to you
              as cash back.
            </p>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900">Questions & answers</h2>
          </div>

          <div className="divide-y divide-gray-100">
            {faqs.map((faq, i) => (
              <div key={i}>
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between py-6 text-left group"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-medium text-gray-900 pr-8 group-hover:text-brand-500 transition-colors">
                    {faq.q}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-45' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${openFaq === i ? 'max-h-48 pb-6' : 'max-h-0'}`}>
                  <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-brand-900 rounded-3xl px-8 py-16 md:px-16 md:py-24 text-center relative overflow-hidden">
            {/* Decorative gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-800/50 via-transparent to-saffron-500/10 pointer-events-none" />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-700/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-saffron-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 leading-tight">
                Ready to grow
                <br />
                your school?
              </h2>
              <p className="text-brand-200 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                Set up in 10 minutes. Your students can enroll and pay the same day. It&rsquo;s free to start.
              </p>
              <button
                onClick={() => signupAs('teacher')}
                className="bg-white text-brand-900 px-10 py-4 rounded-full text-lg font-semibold hover:bg-brand-50 transition-colors hover:shadow-lg"
              >
                Create Your Studio
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-[#111111] text-gray-400 rounded-t-[2rem] mt-8">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-8">
          <div className="flex flex-col md:flex-row gap-12 md:gap-0 mb-16">
            {/* Logo — large, bold */}
            <div className="md:w-2/5">
              <h2 className="font-heading text-4xl md:text-5xl text-white leading-tight">
                <span className="font-bold">DanceHall</span>
                <span className="font-light text-gray-500"> Studios</span>
              </h2>
            </div>

            {/* Links */}
            <div className="md:w-1/5">
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li>
                  <button onClick={() => signupAs('teacher')} className="hover:text-white transition-colors">
                    Get Started
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div className="md:w-1/5">
              <div className="flex items-start gap-2 text-sm mb-3">
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5M3.75 3v18m16.5-18v18M5.25 3h13.5M6 6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75M6 18.75h12" />
                </svg>
                <a href="https://www.cbblabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  CBB Labs Inc.
                </a>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <span>447 Broadway FL 2, New York, NY</span>
              </div>
            </div>

            {/* Follow Us */}
            <div className="md:w-1/5 md:text-right">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Follow Us</p>
              <div className="flex items-center gap-3 md:justify-end">
                <a
                  href="https://www.instagram.com/my.dancehall/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:border-white hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} DanceHall Studios. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
